/* global google */
import { useRef, useEffect } from "react";
import GoogleMapReact from "google-map-react";
import PointerMap from "../../assets/img/svg/providers/markerGreen.svg";
import PointerMapDisable from "../../assets/img/svg/providers/MarkerRed.svg";
import PointerMapActivo from "../../assets/img/svg/providers/markerProveedorActivo.svg";
import PointerMapInactivo from "../../assets/img/svg/providers/markerProveedorInactivo.svg";
import PointerMapEnProceso from "../../assets/img/svg/providers/markerProveedorEnProceso.svg";

import { formatName } from "../../utils/functionsFormat";

export default function Map({
  markers = [],
  zoom = 5,
  installersStatus = false,
  height = "100vh",
}) {
  const mapRef = useRef(null); // Referencia al mapa
  const mapsRef = useRef(null); // Referencia a la API de Google Maps
  const markersRef = useRef([]); // Referencia a los marcadores
  const infoWindowRef = useRef(null);

  // Función para crear marcadores
  const createMarkers = () => {
    if (mapRef.current && mapsRef.current) {
      // Limpiar marcadores existentes
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];

      // Crear nuevos marcadores
      markers
        .filter((marker) => marker.lat !== null && marker.lng !== null) // Filtrar nulos
        .forEach((marker) => {
          const iconUrl = installersStatus
            ? typeof marker.icon === "string" &&
              marker.icon.includes("instalador_activo")
              ? PointerMap
              : PointerMapDisable
            : marker.icon || PointerMap;

          const newMarker = new mapsRef.current.Marker({
            position: { lat: marker.lat, lng: marker.lng },
            map: mapRef.current,
            icon: {
              url: iconUrl,
              scaledSize: new mapsRef.current.Size(20, 20), // Ajustar tamaño del ícono
            },
          });

          if (marker.info) {
            let contentHtml = "";

            if (marker.info.gafete) {
              // Instalador
              contentHtml = `
              <div class="custom-tooltip-installer">
                <div class="custom-tooltip-header">
                  <span class="custom-tooltip-status">
                    <img src="${
                      marker.info.status === "Activo"
                        ? PointerMap
                        : PointerMapDisable
                    }" alt=" " />
                    <span class="custom-tooltip-status-button">
                      ${marker.info.status || "Desconocido"}
                    </span>
                  </span>
                </div>
                <div class="custom-tooltip-box">
                <p class="custom-tooltip-name">
                  ${formatName(marker.info.nombre) || "Sin nombre"}
                </p>
            
                <div class="custom-tooltip-items">
                  <p><b>Gafete:</b> ${marker.info.gafete}</p>
                  <p><b>Empresa:</b> ${formatName(marker.info.empresa)}</p>
                  <p><b>Última conexión:</b> ${
                    marker.info.fecha || "Desconocido"
                  }</p>
                </div>
                </div>
              </div>
            `;
            } else if (marker.info.esProyecto) {
              // Proyectos
              contentHtml = `
              <div class="custom-tooltip-installer">
                <div class="custom-tooltip-header">
                  <span class="custom-tooltip-status">
                    <img src="${marker.icon}" alt=" " />
                    <span class="custom-tooltip-status-button">
                      ${marker.info.status || "Desconocido"}
                    </span>
                  </span>
                </div>
                <div class="custom-tooltip-box">
                <p class="custom-tooltip-name">
                  ${formatName(marker.info.nombre) || "Sin nombre"}
                </p>
            
                <div class="custom-tooltip-items">
                  <p><b>Proyecto:</b> ${marker.info.proyecto}</p>
                  <p><b>Permisionario:</b> ${formatName(
                    marker.info.permisionario
                  )}</p>
                  <p><b>Cliente:</b> ${formatName(marker.info.cliente)}</p>
                  <p><b>RPU:</b> ${marker.info.rpu || ""}</p>
                  <p><b>Dirección:</b> ${marker.info.direccion || ""}</p>
                </div>
                </div>
              </div>
            `;
            } else {
              // Empresa
              contentHtml = `<div class="custom-tooltip-installer">
                <div class="custom-tooltip-header">
                  <span class="custom-tooltip-status">
                  <img src="${
                    marker.info.status === "activo"
                      ? PointerMapActivo
                      : marker.info.status === "inactivo"
                      ? PointerMapInactivo
                      : PointerMapEnProceso
                  }" alt=" " />

                    <span class="custom-tooltip-status-button">
                      ${marker.info.status || "Desconocido"}
                    </span>
                  </span>
                </div>
                <div class="custom-tooltip-box">
                  <p class="custom-tooltip-name">
                    ${formatName(marker.info.razonSocial) || "Sin nombre"}
                  </p>
                  <div class="custom-tooltip-items">
                    <p><b>Dirección:</b> ${
                      marker.info.direccion || "Sin dirección"
                    }</p>
                  </div>
                </div>
              </div>
              `;
            }

            const infoWindow = new mapsRef.current.InfoWindow({
              content: contentHtml,
            });

            newMarker.addListener("click", () => {
              // Cerrar cualquier InfoWindow abierta antes
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
              }

              infoWindow.open(mapRef.current, newMarker);
              infoWindowRef.current = infoWindow;
              infoWindow.addListener("domready", () => {
                const iwOuter = document.querySelector(".gm-style-iw-c");
                if (iwOuter) iwOuter.style.padding = "0";

                const iwInner = document.querySelector(".gm-style-iw-chr");
                if (iwInner) iwInner.style.background = "#f9f9f9";
              });
            });
          }

          markersRef.current.push(newMarker);
        });
    }
  };

  // Actualizar marcadores cuando cambien los datos
  useEffect(() => {
    if (mapRef.current && mapsRef.current && markers.length > 0) {
      createMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, mapRef.current, mapsRef.current]); // Se ejecuta cada vez que `markers` cambia

  // Inicializar el mapa y guardar referencias
  const handleApiLoaded = ({ map, maps }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    createMarkers(); // Crear marcadores iniciales
  };

  const defaultProps = {
    center: { lat: 19.445164, lng: -99.110556 }, // Valor por defecto
    zoom: zoom,
  };

  useEffect(() => {
    // Limpiar marcadores al desmontar el componente
    return () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="map-container" style={{ height: height, width: "100%" }}>
      <GoogleMapReact
        bootstrapURLKeys={{
          key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        }}
        defaultCenter={defaultProps.center}
        defaultZoom={defaultProps.zoom}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded} // Cargar los marcadores aquí
      />
    </div>
  );
}
